import { useNavigate } from "react-router-dom";
import type { VerificationStatusMap, DocumentRow } from "../../../types/documents";

interface VerificationStatusBadgesProps {
  status: VerificationStatusMap;
  /** Optional document item for navigation - when provided, badges become clickable */
  item?: DocumentRow;
}

/**
 * Color-name → Tailwind class for the verification dot.
 *
 * Backend sends a wider palette than just green/yellow/red — see
 * ocr-ui/src/shared/table/chips-cell.jsx LIGHT_COLOR_MAP for the full
 * list. We accept every value the reference accepts so the dot doesn't
 * silently degrade to gray when the backend stamps "emerald", "blue",
 * etc. Anything unknown still falls through to gray-300 via the `??` in
 * the render path.
 */
const DOT: Record<string, string> = {
  green: "bg-emerald-500",
  emerald: "bg-emerald-500",
  yellow: "bg-amber-400",
  amber: "bg-amber-400",
  orange: "bg-orange-500",
  red: "bg-rose-500",
  rose: "bg-rose-500",
  blue: "bg-blue-500",
  cyan: "bg-cyan-500",
  white: "bg-gray-300",
};

/**
 * Color-name → Tailwind pill classes (bg / text / ring) for the chip
 * around the dot. Mirrors the reference's LIGHT_COLOR_MAP in
 * ocr-ui/src/shared/table/chips-cell.jsx: a tinted light background
 * with a matching darker text. We keep `orange` for stages the
 * pipeline is actively working on (matches the "Config Layout" pill
 * in the Layout Config reference design) and use the same hue family
 * for the dot.
 */
const PILL: Record<string, string> = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  yellow: "bg-amber-50 text-amber-700 ring-amber-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  orange: "bg-orange-50 text-orange-600 ring-orange-200",
  red: "bg-rose-50 text-rose-700 ring-rose-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
};

/** Fallback (unset / "white" / unknown signal) pill + dot styling. */
const UNSET_PILL = "bg-gray-50 text-gray-500 ring-gray-200";
const UNSET_DOT = "bg-gray-300";

/**
 * User-facing labels for each verification code. These replace the
 * short codes on the badge itself — product wants the descriptive names
 * shown in the Smart Inbox and detail panel rather than the abbreviations.
 */
const LONG: Record<string, string> = {
  TV: "Verify AI Template",
  BC: "Rule Verification",
  BR: "Final Checker",
  RC: "Reconciliation",
};

/** Clickable verification codes that support navigation */
const CLICKABLE_CODES = ["TV", "BC", "BR"];

/**
 * Renders the parsed `verification_status` map as a row of small dots, one
 * per code (TV, BC, BR, …). Tooltip on hover shows the long name + signal.
 * When `item` is provided, badges become clickable and navigate to verification.
 */
export function VerificationStatusBadges({ status, item }: VerificationStatusBadgesProps) {
  const navigate = useNavigate();
  const codes = Object.keys(status);

  const handleBadgeClick = (code: string) => {
    if (!item) return;

    const upperCode = code.toUpperCase();

    // Build query params for navigation (matching buildVerifyHref format)
    const params = new URLSearchParams({ id: item.process_id || "" });
    if (item.workflow_id) params.set("workflow_id", item.workflow_id);
    if (item.uploader) params.set("uploader", item.uploader);
    if (item.filename) params.set("filename", item.filename);
    if (item.layout_id) params.set("layout_id", item.layout_id);

    // Navigate based on verification code
    if (upperCode === "TV") {
      // Tools View - navigate to verification (same as "Open in Verification" button)
      navigate(`/ocr-tracking/verify?${params.toString()}`);
    } else if (upperCode === "BC") {
      // Business Check (Verify Rule) - navigate to business rules page
      navigate(`/ocr-tracking/business-rules?${params.toString()}`, {
        state: {
          process_id: item.process_id,
          layout_id: item.layout_id,
          workflow_id: item.workflow_id,
        },
      });
    } else if (upperCode === "BR") {
      // Business Rule (Final Checker) - navigate to verification page
      navigate(`/ocr-tracking/verify?${params.toString()}`);
    }
  };

  if (codes.length === 0) return <span className="text-xs text-gray-400">—</span>;

  return (
    <div className="flex items-center gap-1">
      {codes.map((code) => {
        const signal = String(status[code] ?? "").toLowerCase();
        const long = LONG[code] ?? code;
        const isClickable = item && CLICKABLE_CODES.includes(code.toUpperCase());
        // Pill + dot both pick from the same signal so a "green" stage
        // pops emerald, "yellow" goes amber, "red" goes rose, etc. The
        // "white" sentinel and unrecognised values fall through to the
        // muted gray styling the reference uses for inactive chips.
        const pillTone = PILL[signal] ?? UNSET_PILL;
        const dotColor = DOT[signal] ?? UNSET_DOT;

        return (
          <span
            key={code}
            title={`${long}: ${signal || "unset"}`}
            onClick={isClickable ? (e) => { e.stopPropagation(); handleBadgeClick(code); } : undefined}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ring-1 ring-inset ${pillTone} ${isClickable ? "cursor-pointer hover:brightness-95" : ""}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
            {long}
          </span>
        );
      })}
    </div>
  );
}
